/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";

import {
    Alert,
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Fade,
    Grid,
    IconButton,
    Modal,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { CreateAssetRequest, Effect, ModalBase, Nullable } from "../../utils/Interfaces";
import { delayedAction } from "../../utils/Functions";
import { AssetType, AssetTypeMap, assetTypeMappings, lookupAssetType } from "../../utils/Constants";
import CloseIcon from "@mui/icons-material/Close";

import { NumericFormat } from "react-number-format";
import { createAsset } from "./assetsFunctions";
import { useAppDispatch } from "../../app/hooks";

export interface ModifyAssetModalProps extends ModalBase {
    onAssetCreated: Effect;
}

const ModifyAssetModal = (props: ModifyAssetModalProps) => {
    const { open, handleClose, onAssetCreated } = props;
    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const theme = useTheme();
    const [loading, setLoading] = useState<boolean>(false);

    const [name, setName] = useState<string>("");
    const [manufacturer, setManufacturer] = useState<string>("");
    const [partNumber, setPartNumber] = useState<string>("");
    const [price, setPrice] = useState<number>(0.0);
    const [notes, setNotes] = useState<Nullable<string>>(null);
    const [serialNumber, setSerialNumber] = useState<Nullable<string>>(null);
    const [assetType, setAssetType] = useState<AssetType>(AssetType.TOOL);

    const [amount, setAmount] = useState<number>(1);

    function clearAssetValues(): void {
        setName("");
        setManufacturer("");
        setPartNumber("");
        setPrice(0.0);
        setNotes(null);
        setSerialNumber(null);
        setAssetType(AssetType.TOOL);
        setAmount(1);
    }

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            clearAssetValues();
        });
    }

    function getFormErrors(): string {
        if (name === null || name.trim() === "") {
            return "Asset name cannot be blank.";
        }
        if (manufacturer === null || manufacturer.trim() === "") {
            return "Asset manufacturer cannot be blank.";
        }
        if (partNumber === null || partNumber.trim() === "") {
            return "Asset part number cannot be blank.";
        }

        if (amount > 100) {
            return "Cannot bulk create more than 100 assets at a time.";
        }

        return "";
    }

    function getFormWarnings(): string {
        // Only show warnings if errors aren't present.
        if (getFormErrors() !== "") {
            return "";
        }

        if (assetType !== AssetType.CONSUMABLE && (serialNumber === null || serialNumber.trim() === "")) {
            return "Where possible, serial numbers should be applied to non-consumables.";
        }

        if ((serialNumber?.toLowerCase().trim() ?? "") === "n/a" || (serialNumber?.toLowerCase().trim() ?? "") === "na") {
            return "It is recommended to leave the Serial # field blank if it's not applicable.";
        }

        if (amount > 10) {
            return "You are about to create more than 10 assets.";
        }

        return "";
    }

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        width: "100%",
                        maxHeight: "90vh",
                        maxWidth: "1800px",
                        backgroundColor: theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: theme.shadows[5],
                        padding: theme.spacing(2, 4, 3),
                        margin: theme.spacing(2, 4, 3),
                        zIndex: 1000,
                        overflowY: "auto",
                        position: "relative",
                    }}
                >
                    <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="h5" textAlign="center" sx={{ ml: 2, mb: 2, width: "100%" }}>
                                Enter New Asset
                            </Typography>
                            <Stack direction="column" justifyContent="flex-start" sx={{ mr: -2 }}>
                                <IconButton aria-label="delete" onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Stack>

                        {getFormErrors() !== "" && <Alert severity="error">{getFormErrors()}</Alert>}

                        {getFormWarnings() !== "" && <Alert severity="warning">{getFormWarnings()}</Alert>}

                        {getFormErrors() === "" && getFormWarnings() === "" && (
                            <Alert severity="success">Form entries are valid.</Alert>
                        )}

                        <Grid container>
                            <Grid xs={6} sx={{ p: 1 }} item>
                                <TextField
                                    fullWidth
                                    sx={{ m: 1 }}
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    label="Asset Name"
                                    name="asset_name"
                                />
                            </Grid>

                            <Grid xs={6} sx={{ p: 1 }} item>
                                <TextField
                                    fullWidth
                                    sx={{ m: 1 }}
                                    value={manufacturer}
                                    onChange={(event) => setManufacturer(event.target.value)}
                                    label="Asset Manufacturer"
                                    name="asset_manufacturer"
                                />
                            </Grid>

                            <Grid xs={6} sx={{ p: 1 }} item>
                                <TextField
                                    fullWidth
                                    sx={{ m: 1 }}
                                    value={partNumber}
                                    onChange={(event) => setPartNumber(event.target.value)}
                                    label="Asset Part #"
                                    name="asset_part_number"
                                    InputProps={{ sx: { fontFamily: '"Tahoma", sans-serif' } }}
                                />
                            </Grid>

                            <Grid xs={6} sx={{ p: 1 }} item>
                                <TextField
                                    disabled={amount > 1}
                                    fullWidth
                                    sx={{ m: 1 }}
                                    value={serialNumber}
                                    onChange={(event) => {
                                        setSerialNumber(event.target.value);
                                        if (event.target.value.trim() !== "") {
                                            setAmount(1);
                                        }
                                    }}
                                    label="Asset Serial #"
                                    name="asset_serial_number"
                                    InputProps={{ sx: { fontFamily: '"Tahoma", sans-serif' } }}
                                />
                            </Grid>

                            <Grid xs={6} sx={{ p: 1 }} item>
                                <NumericFormat
                                    allowNegative={false}
                                    value={price}
                                    customInput={TextField}
                                    style={{ marginLeft: 8, marginTop: 8, width: "100%" }}
                                    label="Price"
                                    thousandSeparator=","
                                    decimalScale={2}
                                    prefix="$ "
                                    onValueChange={(values) => {
                                        setPrice(values.floatValue ?? 0.0);
                                    }}
                                />
                            </Grid>

                            <Grid xs={6} sx={{ p: 1 }} item>
                                <Autocomplete
                                    fullWidth
                                    disablePortal
                                    onChange={(_event, value) => setAssetType(value ? value.value : AssetType.TOOL)}
                                    id="asset_type"
                                    title="Asset Type"
                                    getOptionKey={(option: AssetTypeMap) => option.value}
                                    options={assetTypeMappings}
                                    value={lookupAssetType(assetType)}
                                    blurOnSelect={true}
                                    autoHighlight={true}
                                    getOptionLabel={(option: AssetTypeMap) => option.name}
                                    renderInput={(params) => (
                                        <TextField {...params} sx={{ m: 1 }} fullWidth label="Select Asset Type" />
                                    )}
                                />
                            </Grid>

                            <Grid xs={6} sx={{ p: 1 }} item>
                                <NumericFormat
                                    allowNegative={false}
                                    isAllowed={(values) => (values.floatValue ?? 1) <= 100}
                                    disabled={serialNumber ? serialNumber.trim() !== "" : false}
                                    value={amount}
                                    customInput={TextField}
                                    style={{ marginLeft: 8, marginTop: 8, width: "100%" }}
                                    label="Amount to Create"
                                    thousandSeparator=","
                                    decimalScale={0}
                                    prefix=""
                                    max={10}
                                    onValueChange={(values) => {
                                        if ((values.floatValue ?? 1) > 1) {
                                            setSerialNumber("");
                                        }
                                        setAmount(values.floatValue ?? 1);
                                    }}
                                />
                            </Grid>

                            <Grid xs={12} sx={{ p: 1 }} item>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    sx={{ m: 1 }}
                                    value={notes}
                                    onChange={(event) => setNotes(event.target.value)}
                                    label="Asset Notes"
                                    name="asset_notes"
                                />
                            </Grid>
                        </Grid>

                        {loading && (
                            <Stack direction="row" justifyContent="center">
                                <CircularProgress size={16} sx={{ mt: 2.7, mb: 2 }} />
                            </Stack>
                        )}

                        {!loading && (
                            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                                <Tooltip title="Discard changes">
                                    <Button variant="contained" color="secondary" sx={{ mr: 1 }} onClick={onClose}>
                                        Cancel
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Update material">
                                    <Button
                                        disabled={getFormErrors() !== ""}
                                        variant="contained"
                                        color="success"
                                        sx={{ ml: 1 }}
                                        onClick={() => {
                                            let payload: CreateAssetRequest = {
                                                amount: amount,
                                                name: name.trim(),
                                                manufacturer: manufacturer.trim(),
                                                part_number: partNumber.trim(),
                                                price: price,
                                                serial_number: !serialNumber ? null : serialNumber.trim(),
                                                asset_type: assetType,
                                                notes: !notes ? null : notes.trim(),
                                            };
                                            createAsset(
                                                payload,
                                                dispatch,
                                                () => {
                                                    onAssetCreated();
                                                    onClose();
                                                },
                                                setLoading
                                            );
                                        }}
                                    >
                                        Create Asset{amount > 1 ? "s" : ""}
                                    </Button>
                                </Tooltip>
                            </Stack>
                        )}
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default ModifyAssetModal;
